import { z } from 'zod';
import { checkAnswer } from './questions.js';
/**
 * Renewal campaigns (task 69, decision 0045). An insurer picks clients by renewal window (or any clients
 * filter) and starts a renewal check for each at once. Each check carries the client's last answers
 * forward as "still true?" confirmations: the business confirms them in one go, or changes the ones that
 * changed. A campaign can also start checks by itself, a set number of days before each client's renewal.
 */
export const CreateCampaign = z.strictObject({
    name: z.string().trim().min(1).max(80),
    /** The clients list query that picks the clients (e.g. "renewing=60&lifecycle=bound"), without paging. */
    query: z
        .string()
        .max(1000)
        .transform((q) => q.replace(/^[?#]/, ''))
        .refine((q) => !/(^|&)(page|page_size|view)=/.test(q), 'Leave out the page and view'),
    /** Start the checks now for these clients (picked from the preview); empty starts none now. */
    start: z.array(z.string().min(1).max(200)).max(500).default([]),
    /** Carry the last answers forward to confirm. */
    carry_forward: z.boolean().default(true),
    /** Also start a check for every client the query matches this many days before its renewal. */
    auto_days: z.number().int().min(7).max(180).nullable().default(null),
});
export const AddToCampaign = z.strictObject({ start: z.array(z.string().min(1).max(200)).min(1).max(500) });
export const ConfirmCarried = z.strictObject({
    /** The questions to confirm as still true; omitted confirms every open one. */
    questions: z.array(z.string().min(1).max(64)).max(500).optional(),
});
/**
 * The answers of the last check that can be carried into a new one: answered (not cleared), still a
 * question in the new configuration, and an answer people may give themselves (checked the way the form
 * would check it). Answers only the system or a connected service gives are left to them.
 */
export function carryable(previous, questions) {
    const out = [];
    for (const [key, a] of previous) {
        const q = questions[key];
        if (!q || a.cleared || a.value === null || a.value === undefined)
            continue;
        const checked = checkAnswer(q, a.value, 'USER_DECLARED');
        if (checked.ok)
            out.push({ question: key, value: checked.value });
    }
    return out.sort((x, y) => x.question.localeCompare(y.question));
}
export const sameAnswer = (a, b) => JSON.stringify(a) === JSON.stringify(b);
//# sourceMappingURL=campaigns.js.map